import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import config from '../config';
import Loading from '../components/Loading';
import {
  FootballPattern,
  overlayStyle,
  CountdownTimer,
  GroupStandings,
  OngoingMatches,
  TopScorers,
  EmptyStateMessage,
  FeaturedMatches,
  FinishedKnockout,
  RegisteredTeams,
  UpcomingMatches,
  COLORS,
} from '../components/MAMC';

// TabPanel component for rendering tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`mamc-tabpanel-${index}`}
      aria-labelledby={`mamc-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const MAMC = () => {
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [scores, setScores] = useState([]);
  const [scorers, setScorers] = useState([]);
  const [teamStats, setTeamStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [adminConfigs, setAdminConfigs] = useState({
    showGroups: true,
    isRegistrationOngoing: true,
  });
  const [notificationStatus, setNotificationStatus] = useState('idle'); // idle | enabled | denied | error
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // State to manage visibility of the notification banner
  const [showNotificationBanner, setShowNotificationBanner] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  // Tab names for URL anchors
  const tabNames = ['table', 'teams', 'matches', 'results', 'top-scorers'];

  // State to store the beforeinstallprompt event
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Listen for the beforeinstallprompt event
  useEffect(() => {
    const handler = e => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      console.log('beforeinstallprompt event fired.', e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []); // Empty dependency array means this effect runs only once after initial render

  // Fetch admin configs
  const fetchAdminConfigs = async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/admin-configs`);
      if (response.ok) {
        const data = await response.json();
        setAdminConfigs(data);
      }
    } catch (error) {
      console.error('Error fetching admin configs:', error);
    }
  };

  // Fetch all teams
  const fetchTeams = async year => {
    try {
      setLoading(true);
      const yearParam = year ? `?year=${year}` : '';
      const response = await fetch(`${config.apiBaseUrl}/teams${yearParam}`);
      if (response.ok) {
        const data = await response.json();

        // Fetch players for each team
        const teamsWithPlayers = await Promise.all(
          data.map(async team => {
            try {
              const playersResponse = await fetch(`${config.apiBaseUrl}/players/team/${team.id}`);

              if (playersResponse.ok) {
                const players = await playersResponse.json();
                return { ...team, players };
              }
              return team;
            } catch (error) {
              console.error(`Error fetching players for team ${team.id}:`, error);
              return team;
            }
          })
        );

        setTeams(teamsWithPlayers);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  // Fetch all matches
  const fetchMatches = async year => {
    try {
      const yearParam = year ? `?year=${year}` : '';
      const response = await fetch(`${config.apiBaseUrl}/matches${yearParam}`);
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  // Fetch all scores
  const fetchScores = async year => {
    try {
      const yearParam = year ? `?year=${year}` : '';
      const response = await fetch(`${config.apiBaseUrl}/scores${yearParam}`);
      if (response.ok) {
        const data = await response.json();
        setScores(data);
      }
    } catch (error) {
      console.error('Error fetching scores:', error);
    }
  };

  // Fetch all scorers
  const fetchScorers = async year => {
    try {
      const yearParam = year ? `?year=${year}` : '';
      const response = await fetch(`${config.apiBaseUrl}/scorers${yearParam}`);
      if (response.ok) {
        const data = await response.json();

        // Enhance scorer data with player information if not already present
        const enhancedScorers = await Promise.all(
          data.map(async scorer => {
            if (scorer.player && scorer.player.player_name) {
              return scorer;
            }

            try {
              // If scorer doesn't have player info, fetch the player
              const playerResponse = await fetch(
                `${config.apiBaseUrl}/players/${scorer.player_id}`
              );

              if (playerResponse.ok) {
                const player = await playerResponse.json();
                return { ...scorer, player };
              }

              // If player endpoint fails, try to find the player in team players
              const playerFromTeams = teams
                .flatMap(team => team.players || [])
                .find(player => player.id === scorer.player_id);

              if (playerFromTeams) {
                return { ...scorer, player: playerFromTeams };
              }
            } catch (error) {
              console.error(`Error fetching player for scorer ${scorer.id}:`, error);
            }

            // If all else fails, create a placeholder player
            return {
              ...scorer,
              player: {
                id: scorer.player_id,
                player_name: `Player ${scorer.player_id}`,
              },
            };
          })
        );

        setScorers(enhancedScorers);
      }
    } catch (error) {
      console.error('Error fetching scorers:', error);
    }
  };

  // Calculate stats for a team
  const calculateTeamStats = async teamId => {
    // Only include group stage matches (matches without a type or type === 'Group Stage')
    const teamMatches = matches.filter(
      match =>
        (match.home_team_id === teamId || match.away_team_id === teamId) &&
        (!match.type || match.type === 'Group stage')
    );

    let matchesPlayed = 0;
    let wins = 0;
    let draws = 0;
    let losses = 0;
    let goalsFor = 0;
    let goalsAgainst = 0;
    let points = 0;
    let matchHistory = [];

    teamMatches.forEach(match => {
      const score = scores.find(s => s.match_id === match.id);
      if (score) {
        matchesPlayed++;
        const isHomeTeam = match.home_team_id === teamId;
        const teamScore = isHomeTeam ? score.home_team_score : score.away_team_score;
        const opponentScore = isHomeTeam ? score.away_team_score : score.home_team_score;

        goalsFor += teamScore;
        goalsAgainst += opponentScore;

        if (teamScore > opponentScore) {
          wins++;
          points += 3;
        } else if (teamScore === opponentScore) {
          draws++;
          points += 1;
        } else {
          losses++;
        }

        // Add to match history
        matchHistory.push({
          date: match.time,
          isHomeTeam,
          homeTeamId: match.home_team_id,
          awayTeamId: match.away_team_id,
          homeTeamScore: score.home_team_score,
          awayTeamScore: score.away_team_score,
        });
      }
    });

    // Sort matches by date (newest first) and take last 5
    matchHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
    matchHistory = matchHistory.slice(0, 5);

    return {
      matchesPlayed,
      wins,
      draws,
      losses,
      goalsFor,
      goalsAgainst,
      goalDifference: goalsFor - goalsAgainst,
      points,
      matchHistory,
    };
  };

  // Calculate stats for all teams
  const calculateAllTeamStats = async () => {
    const stats = {};

    for (const team of teams) {
      stats[team.id] = await calculateTeamStats(team.id);
    }

    setTeamStats(stats);
  };

  // Fetch all necessary data
  const fetchAllData = async year => {
    setLoading(true);
    await fetchAdminConfigs();
    await fetchTeams(year);
    await fetchMatches(year);
    await fetchScores(year);
    await fetchScorers(year);
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData(selectedYear);
  }, [selectedYear]);

  // Handle initial URL hash on page load
  useEffect(() => {
    const hash = window.location.hash.substring(1); // Remove the '#'
    const tabIndex = tabNames.indexOf(hash);
    if (tabIndex !== -1) {
      setSelectedTab(tabIndex);
    }
  }, []);

  // Listen for hash changes (back/forward browser navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      const tabIndex = tabNames.indexOf(hash);
      if (tabIndex !== -1) {
        setSelectedTab(tabIndex);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Calculate stats after data is loaded
  useEffect(() => {
    const calculateStats = async () => {
      if (teams.length > 0 && matches.length > 0 && scores.length > 0) {
        setLoading(true);
        try {
          await calculateAllTeamStats();
        } finally {
          setLoading(false);
        }
      }
    };

    calculateStats();
  }, [teams, matches, scores]);

  // Group teams by their group field
  const groupedTeams = teams.reduce((acc, team) => {
    if (team.group) {
      if (!acc[team.group]) {
        acc[team.group] = [];
      }
      acc[team.group].push(team);
    }
    return acc;
  }, {});

  // Check if there's data to display
  const hasTeams = teams.length > 0;
  const hasMatches = matches.length > 0;
  const hasOngoingMatches = matches.filter(m => m.ongoing).length > 0;
  const hasGroupedTeams = Object.keys(groupedTeams).length > 0;
  const hasScorers = scorers.length > 0;
  const hasFeaturedMatches = matches.some(
    m => (m.type === 'Semi Final' || m.type === 'Final') && !m.isFinished
  );
  const hasFinishedKnockout = matches.some(m => m.isFinished && m.type && m.type !== 'Group stage');
  const hasUpcomingMatches = matches.some(
    m => !m.isFinished && !m.ongoing && m.type !== 'Semi Final' && m.type !== 'Final'
  );

  // Check if any Semi Finals or Finals are currently ongoing
  const hasOngoingChampionshipMatches = matches.some(
    m => (m.type === 'Semi Final' || m.type === 'Final') && m.ongoing
  );

  // Helper to convert VAPID key
  function urlBase64ToUint8Array(base64String) {
    // Remove any whitespace
    base64String = base64String.trim();

    // Remove any URL-safe base64 characters and replace with standard base64
    const base64 = base64String.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const paddedBase64 = base64 + padding;

    // Convert to binary string
    const binaryString = window.atob(paddedBase64);

    // Convert to Uint8Array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    console.log('Converted VAPID key:', {
      original: base64String,
      padded: paddedBase64,
      length: bytes.length,
      bytes: Array.from(bytes).slice(0, 10) + '...', // Log first 10 bytes for debugging
    });

    return bytes;
  }

  const handleEnableNotifications = async () => {
    console.log('Enable Notifications button clicked.');

    if (deferredPrompt) {
      console.log('deferredPrompt found, prompting installation...');
      // Show the install prompt
      deferredPrompt.prompt();
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      // We've used the prompt, and can't use it again,
      // so remove it from the state
      setDeferredPrompt(null);

      console.log(`User response to the install prompt: ${outcome}`);
      if (outcome === 'accepted') {
        setSnackbarMessage('App installed successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('App installation dismissed.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
      // If we prompted for installation, we don't proceed with notification subscription here.
      // The user needs to launch the installed app first.
      return; // Exit the function after handling the install prompt
    }

    // If no deferredPrompt is available (e.g., already installed, not supported)
    // proceed with the original notification subscription logic.
    console.log('No deferredPrompt found, proceeding with notification subscription...');

    // Check for notification support and permission status
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.error('Service Worker or Push Manager not supported');
      setNotificationStatus('error');
      setSnackbarMessage(
        'Push notifications are not supported in your browser. Please try using Chrome, Firefox, or Edge on desktop.'
      );
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    try {
      console.log('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('Permission status:', permission);

      if (permission !== 'granted') {
        console.log('Notification permission denied');
        setNotificationStatus('denied');
        setSnackbarMessage('Notification permission denied.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }

      console.log('Notification permission granted');
      console.log('Getting service worker registration...');
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', registration);

      // Wait for the service worker to be active
      let swRegistration = registration;
      if (!registration.active) {
        swRegistration = await navigator.serviceWorker.ready;
      }

      // Get VAPID public key from backend
      console.log('Fetching VAPID public key...');
      const vapidRes = await fetch(`${config.apiBaseUrl}/notifications/public-key`);
      if (!vapidRes.ok) {
        console.error('Failed to get VAPID key:', vapidRes.status, vapidRes.statusText);
        throw new Error(`Failed to get VAPID key: ${vapidRes.statusText}`);
      }
      const { publicKey } = await vapidRes.json();
      console.log('Got VAPID public key:', publicKey);

      // Subscribe for push
      console.log('Subscribing to push notifications...');
      let pushSubscription;
      try {
        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        console.log('Converted application server key:', applicationServerKey);
        pushSubscription = await swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
        console.log('Raw push subscription:', pushSubscription);
      } catch (subscribeError) {
        console.error('Error creating push subscription:', subscribeError);
        throw new Error('Failed to create push subscription: ' + subscribeError.message);
      }

      if (!pushSubscription) {
        throw new Error('Push subscription is null or undefined');
      }

      // Get the subscription details
      const subscriptionDetails = pushSubscription.toJSON();
      console.log('Subscription details:', subscriptionDetails);

      if (!subscriptionDetails.keys) {
        console.error('Push subscription keys are missing:', subscriptionDetails);
        throw new Error('Push subscription keys are missing');
      }

      // Send subscription to backend
      console.log('Sending subscription to backend...');
      const subscriptionData = {
        endpoint: subscriptionDetails.endpoint,
        keys: subscriptionDetails.keys,
      };
      console.log('Subscription data to send:', subscriptionData);

      const res = await fetch(`${config.apiBaseUrl}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(subscriptionData),
      });

      console.log('Backend response status:', res.status);
      const responseText = await res.text();
      console.log('Backend response text:', responseText);

      if (!res.ok) {
        let errorMessage = 'Failed to subscribe';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || res.statusText;
        } catch (e) {
          errorMessage = responseText || res.statusText;
        }
        console.error('Subscription response error:', errorMessage);
        throw new Error(errorMessage);
      }

      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        responseData = { message: responseText };
      }
      console.log('Subscription response:', responseData);

      console.log('Successfully subscribed to notifications');
      setNotificationStatus('enabled');
      setSnackbarMessage('Successfully subscribed to notifications!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error subscribing to notifications:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
      });
      setNotificationStatus('error');
      setSnackbarMessage('Failed to subscribe to notifications.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Send unsubscribe request to backend
        const res = await fetch(`${config.apiBaseUrl}/notifications/unsubscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });

        if (!res.ok) {
          throw new Error('Failed to unsubscribe from notifications');
        }

        // Unsubscribe from push manager
        await subscription.unsubscribe();
        console.log('Successfully unsubscribed from notifications');
        setNotificationStatus('idle');
        setSnackbarMessage('Successfully unsubscribed from notifications.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error unsubscribing from notifications:', err);
      setNotificationStatus('error');
      setSnackbarMessage('Error unsubscribing from notifications.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Render group standings only if showGroups is true
  const renderGroupStandings = () => {
    if (!adminConfigs.showGroups && selectedYear === new Date().getFullYear()) {
      return null;
    }

    // Get unique groups
    const groups = [...new Set(teams.filter(team => team.group).map(team => team.group))].sort();

    return (
      <>
        {groups.length > 0 ? (
          <Grid container spacing={3} pl={4}>
            {groups.map(group => {
              const teamsInGroup = teams.filter(team => team.group === group);
              return (
                <Grid item xs={12} key={group}>
                  <GroupStandings
                    group={group}
                    teams={teamsInGroup}
                    teamStats={teamStats}
                    matches={matches}
                    scores={scores}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <EmptyStateMessage message="No groups have been created yet." />
        )}
      </>
    );
  };

  const handleCloseBanner = () => {
    setShowNotificationBanner(false);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    // Update URL hash when tab changes
    window.location.hash = tabNames[newValue];
  };

  const handleYearChange = event => {
    setSelectedYear(event.target.value);
  };

  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Generate years from 2024 to 3 years in future
    for (let y = currentYear - 1; y <= currentYear; y++) {
      years.push(y);
    }
    return years.sort((a, b) => b - a); // Sort descending
  };

  return (
    <Box
      sx={{
        bgcolor: '#111111',
        minHeight: '100vh',
        backgroundImage: 'url("/gallery/practise_session.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        ...overlayStyle,
        // Add this to ensure the background color extends to the bottom
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100%', // This ensures it covers the entire height
          background: '#111111',
          zIndex: 0, // Behind content but above the original overlay
        },
      }}
    >
      <FootballPattern />

      {/* Show loading component when fetching data */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
          }}
        >
          <Loading />
        </Box>
      )}

      {/* Hero Section with MAMC Logo - Reduced height */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '180px', sm: '250px', md: '350px' },
          mb: { xs: 2, sm: 3, md: 4 },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to bottom, transparent, #111111)',
            zIndex: 1,
          },
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'radial-gradient(circle at 50% 50%, rgba(218,165,32,0.1) 0%, transparent 70%)',
            zIndex: 0,
          }}
        />

        {/* Logo Container - Adjusted for smaller size */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            opacity: 1,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: { xs: 1, sm: 2, md: 4 },
          }}
        >
          <Box
            component="img"
            src="/events/MAMC_Logo.png"
            alt="MAMC Tournament Logo"
            sx={{
              maxWidth: { xs: '70%', sm: '60%', md: '50%' },
              maxHeight: { xs: '70%', sm: '60%', md: '50%' },
              objectFit: 'contain',
              filter: 'brightness(1.8) contrast(1.4) drop-shadow(0 0 30px rgba(218,165,32,0.5))',
              transform: 'scale(1.1)',
            }}
          />
        </Box>

        {/* Content Container - Made more compact */}
        <Container
          maxWidth="lg"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2,
              p: { xs: 1.5, sm: 2, md: 3 },
              mx: 'auto',
              maxWidth: { xs: '90%', sm: '80%', md: '70%' },
              border: '1px solid rgba(218,165,32,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: '#DAA520',
                fontSize: { xs: '1.25rem', sm: '2rem', md: '3.5rem' },
                fontWeight: 'bold',
                textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                letterSpacing: { xs: '1px', sm: '2px', md: '4px' },
                mb: { xs: 0.5, sm: 1, md: 2 },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -5,
                  left: '25%',
                  width: '50%',
                  height: '2px',
                  background: 'linear-gradient(135deg, #DAA520 0%, #FFD700 100%)',
                },
              }}
            >
              Manoj Aryal Memorial Cup
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: '#ffffff',
                fontSize: { xs: '0.75rem', sm: '1rem', md: '1.5rem' },
                fontWeight: 'normal',
                textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                letterSpacing: { xs: '0.5px', sm: '1px', md: '2px' },
                opacity: 0.9,
                pt: { xs: 1, sm: 1.5, md: 2 },
              }}
            >
              MAMC 2026
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 1, sm: 2, md: 4 },
          position: 'relative',
          zIndex: 2,
          paddingBottom: '50px', // Add padding to the bottom of the container
        }}
      >
        {/* Year Filter Dropdown */}
        <Box sx={{ mb: { xs: 2, sm: 3, md: 4 }, display: 'flex', justifyContent: 'flex-end' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: '#DAA520', '&.Mui-focused': { color: '#DAA520' } }}>
              Year
            </InputLabel>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              label="Year"
              sx={{
                color: '#ffffff',
                backgroundColor: 'rgba(218,165,32,0.1)',
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(218,165,32,0.5)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#DAA520',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#DAA520',
                },
                '& .MuiSvgIcon-root': {
                  color: '#DAA520',
                },
              }}
            >
              {getAvailableYears().map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Countdown Timer - Always shown */}
        <Box sx={{ mb: { xs: 2, sm: 3, md: 5 } }}>
          <CountdownTimer />
        </Box>

        {/* Upcoming Tournament Announcement - Only show when groups are hidden and it's the current year */}
        {!adminConfigs.showGroups && selectedYear === new Date().getFullYear() && (
          <>
            <Box
              sx={{
                background:
                  'linear-gradient(135deg, rgba(218,165,32,0.15) 0%, rgba(218,165,32,0.05) 100%)',
                border: '2px solid rgba(218,165,32,0.4)',
                borderRadius: 3,
                p: { xs: 3, sm: 4, md: 5 },
                mb: { xs: 3, sm: 4, md: 6 },
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#DAA520',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  fontWeight: 'bold',
                  mb: { xs: 2, sm: 3 },
                  textShadow: '0 2px 8px rgba(218,165,32,0.5)',
                  letterSpacing: '2px',
                }}
              >
                Mark Your Calendar!
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  color: '#FFD700',
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
                  fontWeight: 'bold',
                  mb: { xs: 3, sm: 4 },
                  textShadow: '0 2px 8px rgba(255,215,0,0.5)',
                }}
              >
                MAMC 2026
              </Typography>

              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, rgba(255,215,0,0.2) 0%, rgba(218,165,32,0.1) 100%)',
                  border: '2px solid rgba(255,215,0,0.5)',
                  borderRadius: 2,
                  p: { xs: 2, sm: 3 },
                  mb: { xs: 3, sm: 4 },
                  display: 'inline-block',
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    color: '#FFD700',
                    fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 'bold',
                    textShadow: '0 4px 12px rgba(255,215,0,0.6)',
                    letterSpacing: '1px',
                  }}
                >
                  August 1, 2026
                </Typography>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  color: '#ffffff',
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                  mb: { xs: 2, sm: 3 },
                  maxWidth: '800px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  opacity: 0.9,
                }}
              >
                Location: Laaksolahden urheilupuisto
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.7,
                }}
              >
                Get ready for an exciting tournament season! The Manoj Aryal Memorial Cup 2026 is
                coming to Laaksolahden urheilupuisto on August 1st. Teams are invited to register
                and showcase their skills. Be part of this prestigious football tournament!
              </Typography>
            </Box>

            {/* Get Involved Section */}
            <Box
              sx={{
                background:
                  'linear-gradient(135deg, rgba(218,165,32,0.1) 0%, rgba(218,165,32,0.05) 100%)',
                border: '2px solid rgba(218,165,32,0.3)',
                borderRadius: 3,
                p: { xs: 3, sm: 4, md: 5 },
                mb: { xs: 3, sm: 4, md: 6 },
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: '#DAA520',
                  fontSize: { xs: '1.3rem', sm: '1.8rem', md: '2.2rem' },
                  fontWeight: 'bold',
                  mb: { xs: 2, sm: 3 },
                  textShadow: '0 2px 8px rgba(218,165,32,0.5)',
                  letterSpacing: '1px',
                }}
              >
                Join Us!
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: '#ffffff',
                  fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' },
                  mb: { xs: 2, sm: 3 },
                  maxWidth: '800px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  opacity: 0.9,
                }}
              >
                Are you interested in competing or supporting the tournament?
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  mb: { xs: 3, sm: 4 },
                }}
              >
                If you are interested in sponsoring our cause, volunteering, providing feedback, or
                getting involved in this project, feel free to contact us. Together, we can make
                this tournament successful and memorable.
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#DAA520',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  fontWeight: 'bold',
                  textShadow: '0 2px 4px rgba(218,165,32,0.5)',
                }}
              >
                Get in touch with us to be part of MAMC 2026!
              </Typography>
            </Box>
          </>
        )}

        {/* Show registration promotion and teams when registration is ongoing but groups are hidden */}
        {!adminConfigs.showGroups && adminConfigs.isRegistrationOngoing ? (
          <Box sx={{ position: 'relative', zIndex: 3 }}>
            {/* Registration Call-to-Action Section */}
            {/* <Box
              sx={{
                ...SECTION_STYLES.groupStandings,
                p: { xs: 3, sm: 4 },
                mb: { xs: 3, sm: 4, md: 5 },
                textAlign: 'center',
                background:
                  'linear-gradient(135deg, rgba(218,165,32,0.1) 0%, rgba(218,165,32,0.05) 100%)',
                border: '2px solid rgba(218,165,32,0.3)',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: COLORS.text.accent,
                  fontWeight: 'bold',
                  mb: 2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                Join MAMC 2025!
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: COLORS.text.primary,
                  mb: 3,
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Want your team to compete in the Manoj Aryal Memorial Cup? Registration is now open!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: COLORS.text.secondary,
                  mb: 4,
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.7,
                }}
              >
                Sign up your team today and be part of this prestigious tournament. Create your team
                profile, add players, and secure your spot in the competition.
              </Typography>
              <Button
                variant="contained"
                onClick={() => (window.location.href = '/auth')}
                sx={{
                  bgcolor: COLORS.secondary,
                  color: '#000000',
                  px: 4,
                  py: 2,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  boxShadow: '0 4px 15px rgba(218,165,32,0.4)',
                  '&:hover': {
                    bgcolor: '#FFD700',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(218,165,32,0.6)',
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                }}
              >
                Register Your Team Now
              </Button>
            </Box> */}

            <RegisteredTeams teams={teams} />
          </Box>
        ) : !adminConfigs.showGroups && selectedYear === new Date().getFullYear() ? (
          <Box sx={{ position: 'relative', zIndex: 3 }}>
            <EmptyStateMessage message="Groups are currently hidden by administrator." />
          </Box>
        ) : (
          <Box sx={{ position: 'relative', zIndex: 3 }}>
            {/* Year Header */}
            <Box
              sx={{
                textAlign: 'center',
                mb: 4,
                py: 2,
                px: 3,
                borderRadius: '12px',
                background:
                  'linear-gradient(135deg, rgba(218, 165, 32, 0.15) 0%, rgba(218, 165, 32, 0.05) 100%)',
                border: '1px solid rgba(218, 165, 32, 0.3)',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: COLORS.secondary,
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  letterSpacing: '2px',
                }}
              >
                Tournament {selectedYear}
              </Typography>
            </Box>

            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'rgba(218, 165, 32, 0.3)', mb: 3 }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    minWidth: { xs: 'auto', sm: 120 },
                    '&.Mui-selected': {
                      color: '#DAA520',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#DAA520',
                    height: 3,
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#DAA520',
                  },
                }}
              >
                <Tab label="Table" />
                <Tab label="Teams" />
                <Tab label="Matches" />
                <Tab label="Results" />
                <Tab label="Top Scorers" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <TabPanel value={selectedTab} index={0}>
              {/* Table Tab - Group standings */}
              {renderGroupStandings()}
            </TabPanel>

            <TabPanel value={selectedTab} index={1}>
              {/* Teams Tab */}
              <Grid container spacing={{ xs: 4, sm: 4, md: 6 }}>
                {hasTeams && (
                  <Grid item xs={12}>
                    <RegisteredTeams teams={teams} />
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            <TabPanel value={selectedTab} index={2}>
              {/* Matches Tab - Featured matches, ongoing matches, upcoming matches */}
              <Grid container spacing={{ xs: 4, sm: 4, md: 6 }}>
                {/* Featured Matches - Semi Finals and Finals */}
                {hasFeaturedMatches && (
                  <Grid item xs={12}>
                    <FeaturedMatches
                      matches={matches.filter(m => m.type === 'Semi Final' || m.type === 'Final')}
                      teams={teams}
                      scores={scores}
                      scorers={scorers}
                    />
                  </Grid>
                )}

                {/* Ongoing Matches Section */}
                {hasOngoingMatches && !hasOngoingChampionshipMatches && (
                  <Grid item xs={12}>
                    <OngoingMatches
                      matches={matches.filter(m => m.ongoing)}
                      teams={teams}
                      scores={scores}
                      scorers={scorers}
                    />
                  </Grid>
                )}

                {/* Upcoming Matches Section */}
                {hasUpcomingMatches && (
                  <Grid item xs={12}>
                    <UpcomingMatches matches={matches} teams={teams} />
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            <TabPanel value={selectedTab} index={3}>
              {/* Results Tab - Finished matches */}
              <Grid container spacing={{ xs: 4, sm: 4, md: 6 }}>
                {hasFinishedKnockout && (
                  <Grid item xs={12}>
                    <FinishedKnockout
                      matches={matches}
                      teams={teams}
                      scores={scores}
                      scorers={scorers}
                    />
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            <TabPanel value={selectedTab} index={4}>
              {/* Top Scorers Tab */}
              <Grid container spacing={{ xs: 4, sm: 4, md: 6 }}>
                {hasScorers && (
                  <Grid item xs={12}>
                    <TopScorers scorers={scorers} teams={teams} />
                  </Grid>
                )}
              </Grid>
            </TabPanel>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default MAMC;
